import React, { useCallback, useEffect, useRef, useState } from "react";
import { TerraDraw, TerraDrawLineStringMode, TerraDrawPolygonMode, TerraDrawRenderMode, TerraDrawSelectMode, ValidateNotSelfIntersecting } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import { Hand, PenLine, MinusSquare, MousePointer2, Undo2, Trash2, Eraser, Loader2, Check, X, Spline } from "lucide-react";
import { loadGoogleMaps, ringAreaSqFt } from "@/lib/maps";

type Mode = "pan" | "draw" | "exclude" | "edit";
type Kind = "include" | "exclude" | "line";
const TEAL = "#14b8a6", RED = "#ef4444", AMBER = "#f59e0b";
const FT_PER_M = 3.280839895;
const colorOf = (f: any, fallback: Kind) => {
  const k = f.properties?.kind || fallback;
  return k === "exclude" ? RED : k === "line" ? AMBER : TEAL;
};
const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

const HELP: Record<string, string> = {
  pan: "Pan mode: drag with one finger to move the map, pinch to zoom. Drawing is off.",
  draw: "Draw mode: the map is locked. Tap each corner, then double-tap to complete the area. Draw more areas the same way, then press “Finish Measurement”.",
  drawLine: "Draw mode: the map is locked. Tap along the run, then double-tap to complete the line. Draw more lines the same way, then press “Finish Measurement”.",
  exclude: "Exclude mode: outline anything inside your areas that shouldn’t be counted, then double-tap to complete it.",
  edit: "Edit mode: the map is locked. Tap a shape, then drag its points. Use Delete to remove it.",
  paused: "Your unfinished shape is kept. Tap Draw to keep adding points.",
};

function lengthFt(g: any, coords: number[][]) {
  return g.maps.geometry.spherical.computeLength(coords.map(([lng, lat]) => new g.maps.LatLng(lat, lng))) * FT_PER_M;
}

export function MeasureTool({ cfg, unit, address, initial, view, onView, onSubmit, onFail, busy, errors, onEdit, onMapLoad }: any) {
  const isLine = unit === "linear_ft";
  const el = useRef<HTMLDivElement>(null);
  const drawRef = useRef<any>(null);
  const mapRef = useRef<any>(null);
  const gRef = useRef<any>(null);
  const modeObjs = useRef<any>({});
  const kind = useRef<Kind>(isLine ? "line" : "include");
  const modeRef = useRef<Mode>("pan");
  const hist = useRef<any[]>([]);
  const [mode, setModeState] = useState<Mode>("pan");
  const [shapes, setShapes] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [ready, setReady] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [draft, setDraft] = useState(0);
  const [pts, setPts] = useState(0);
  const rawClick = useRef<any>({});
  const pausedRef = useRef<any>(null);
  const [paused, setPaused] = useState<any>(null);

  const feats = () => drawRef.current.getSnapshot().filter((f: any) => ["Polygon", "LineString"].includes(f.geometry.type) && f.properties?.kind);
  const sync = useCallback(() => {
    const g = gRef.current;
    const snap = drawRef.current.getSnapshot();
    const qtyOf = (f: any) => (f.geometry.type === "LineString" ? lengthFt(g, f.geometry.coordinates) : f.geometry.coordinates[0].length >= 4 ? ringAreaSqFt(g, f.geometry.coordinates[0]) : 0);
    const wip = snap.find((f: any) => ["Polygon", "LineString"].includes(f.geometry.type) && !f.properties?.kind && ["polygon", "linestring"].includes(f.properties?.mode));
    setDrawing(!!wip);
    setDraft(wip ? qtyOf(wip) : 0);
    setPts(wip ? modeObjs.current[wip.properties.mode]?.currentCoordinate || 0 : 0);
    setShapes(snap.filter((f: any) => ["Polygon", "LineString"].includes(f.geometry.type) && f.properties?.kind).map((f: any) => ({
      id: f.id, kind: f.properties.kind, label: f.properties.label, geometry: f.geometry, qty: qtyOf(f),
    })));
  }, []);
  const strip = (f: any) => ({ type: "Feature", id: f.id, geometry: JSON.parse(JSON.stringify(f.geometry)),
    properties: { mode: f.geometry.type === "LineString" ? "linestring" : "polygon", kind: f.properties.kind, label: f.properties.label } });
  const push = () => { hist.current.push(feats().map(strip)); if (hist.current.length > 60) hist.current.shift(); onEdit?.(); };
  // Pan mode is the only mode where one-finger gestures move the map.
  const lockMap = (m: Mode) => mapRef.current?.setOptions({ gestureHandling: m === "pan" ? "greedy" : "none", draggable: m === "pan", disableDoubleClickZoom: m !== "pan" });

  useEffect(() => {
    let cancelled = false;
    let draw: any = null;
    const authFail = () => onFail("maps_unavailable");
    window.addEventListener("iqw-maps-auth-failure", authFail);
    (async () => {
      try {
        const g = await loadGoogleMaps(cfg.maps.browser_key);
        const [{ Map }] = await Promise.all([g.maps.importLibrary("maps"), g.maps.importLibrary("geometry"), g.maps.importLibrary("marker")]);
        if (cancelled || !el.current) return;
        gRef.current = g;
        const map = new Map(el.current, {
          center: view?.center || { lat: address.lat, lng: address.lng }, zoom: view?.zoom || 20, mapTypeId: "satellite", tilt: 0, heading: 0,
          mapId: cfg.maps.map_id || "DEMO_MAP_ID", disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy", clickableIcons: false,
        });
        mapRef.current = map;
        onMapLoad?.();
        g.maps.event.addListenerOnce(map, "projection_changed", () => {
          if (cancelled) return;
          const point = { closingPointColor: "#ffffff", closingPointWidth: 10, closingPointOutlineColor: "#0f172a", closingPointOutlineWidth: 2 };
          const polygon = new TerraDrawPolygonMode({
            pointerDistance: 18,
            validation: (f: any, ctx: any) => (ctx?.updateType === "provisional" ? { valid: true } : ValidateNotSelfIntersecting(f)),
            styles: { fillColor: (f: any) => colorOf(f, kind.current), outlineColor: (f: any) => colorOf(f, kind.current), outlineWidth: 3, fillOpacity: 0.35, ...point } as any,
          });
          const linestring = new TerraDrawLineStringMode({
            pointerDistance: 18,
            styles: { lineStringColor: AMBER, lineStringWidth: 4, ...point } as any,
          });
          const select = new TerraDrawSelectMode({
            pointerDistance: 30,
            flags: {
              polygon: { feature: { draggable: false, selfIntersectable: false, coordinates: { midpoints: true, draggable: true, deletable: true } } },
              linestring: { feature: { draggable: false, coordinates: { midpoints: true, draggable: true, deletable: true } } },
            },
            styles: { selectionPointWidth: 10, selectionPointColor: "#ffffff", selectionPointOutlineColor: "#0f172a", selectionPointOutlineWidth: 2, midPointWidth: 7, midPointColor: "#ffffff" } as any,
          });
          // A shape completes only on a double-tap (or the "Done with shape" button), never on a single tap of the first/last point.
          let last = { t: 0, x: 0, y: 0, done: false };
          const guard = (m: any, min: number, closing: (e: any) => boolean) => {
            const orig = m.onClick.bind(m);
            m.onClick = (e: any) => {
              const t = Date.now();
              const near = t - last.t < 450 && Math.hypot(e.containerX - last.x, e.containerY - last.y) < 30;
              // Swallow trailing taps right after a completion (e.g. triple tap) so no stray shape starts.
              if (near && last.done) { last = { ...last, t }; return; }
              last = { t, x: e.containerX, y: e.containerY, done: false };
              if (e.button === "left" && m.currentCoordinate > 0 && near) {
                if (m.currentCoordinate >= min) { m.onKeyUp({ key: "Enter", heldKeys: [], preventDefault: () => {} }); last.done = true; }
                sync(); return;
              }
              try { if (e.button === "left" && closing(e)) return; } catch (_) { /* noop */ }
              orig(e); sync();
            };
            return orig;
          };
          rawClick.current = {
            polygon: guard(polygon, 3, (e) => { const c = (polygon as any).closingPoints.isPolygonClosingPoints(e); return c.isClosing || c.isPreviousClosing; }),
            linestring: guard(linestring, 2, (e) => (linestring as any).closingPoints.isLineStringClosingPoint(e).isClosing),
          };
          const pausedStyle = new TerraDrawRenderMode({ modeName: "paused", styles: { lineStringColor: "#ffffff", lineStringWidth: 3, pointColor: "#ffffff", pointWidth: 6, pointOutlineColor: "#0f172a", pointOutlineWidth: 2 } as any });
          modeObjs.current = { polygon, linestring };
          draw = new TerraDraw({ adapter: new TerraDrawGoogleMapsAdapter({ lib: g.maps, map, coordinatePrecision: 9 }), modes: [polygon, linestring, select, pausedStyle] });
          drawRef.current = draw;
          draw.on("ready", () => {
            if (initial?.length) draw.addFeatures(initial);
            push(); sync(); setReady(true); draw.setMode("static");
          });
          draw.on("finish", (id: any, ctx: any) => {
            if ((ctx?.mode === "polygon" || ctx?.mode === "linestring") && ctx?.action === "draw") {
              const n = feats().filter((f: any) => f.properties.kind === kind.current).length + 1;
              const label = kind.current === "line" ? `Line ${n}` : kind.current === "include" ? `Area ${n}` : `Exclusion ${n}`;
              draw.updateFeatureProperties(id, { kind: kind.current, label });
            }
            lockMap(modeRef.current);
            push(); sync(); setDrawing(false);
          });
          draw.on("change", () => sync());
          draw.on("select", (id: any) => setSelected(id));
          draw.on("deselect", () => setSelected(null));
          draw.start();
        });
      } catch (_) {
        if (!cancelled) onFail("maps_unavailable");
      }
    })();
    return () => {
      cancelled = true; window.removeEventListener("iqw-maps-auth-failure", authFail);
      const m = mapRef.current;
      if (m?.getCenter()) onView?.({ center: m.getCenter().toJSON(), zoom: m.getZoom() });
      try { draw?.stop(); } catch (_) { /* noop */ }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const drawModeName = () => (kind.current === "line" ? "linestring" : "polygon");
  const setPause = (p: any) => { pausedRef.current = p; setPaused(p); };
  const showPaused = () => {
    const p = pausedRef.current, d = drawRef.current;
    if (!p) return;
    const geometry = p.coords.length > 1 ? { type: "LineString", coordinates: p.coords } : { type: "Point", coordinates: p.coords[0] };
    const r = d.addFeatures([{ type: "Feature", geometry, properties: { mode: "paused" } }]);
    p.displayId = r?.[0]?.id;
  };
  const hidePaused = () => { const p = pausedRef.current; if (p?.displayId != null) { try { drawRef.current.removeFeatures([p.displayId]); } catch (_) { /* noop */ } p.displayId = undefined; } };
  // Capture committed points of the in-progress shape before Terra Draw discards it on mode change.
  const capture = () => {
    const name = drawModeName(), mo = modeObjs.current[name];
    const wip = drawRef.current.getSnapshot().find((f: any) => !f.properties?.kind && f.properties?.mode === name && ["Polygon", "LineString"].includes(f.geometry.type));
    if (!wip || !mo?.currentCoordinate) return null;
    const ring = wip.geometry.type === "Polygon" ? wip.geometry.coordinates[0] : wip.geometry.coordinates;
    return { mode: modeRef.current, kind: kind.current, coords: ring.slice(0, mo.currentCoordinate).map((c: number[]) => [...c]) };
  };
  const replay = (p: any) => {
    const name = drawModeName(), mo = modeObjs.current[name];
    p.coords.forEach(([lng, lat]: number[]) => {
      const { x, y } = mo.project(lng, lat);
      rawClick.current[name]({ lng, lat, containerX: x, containerY: y, button: "left", heldKeys: [], isContextMenu: false });
    });
  };
  const switchMode = (m: Mode) => {
    const d = drawRef.current; if (!d) return;
    const from = modeRef.current;
    if (m === from && (m === "draw" || m === "exclude")) return;
    const wip = drawing && (from === "draw" || from === "exclude") ? capture() : null;
    // Draw <-> Exclude with an unfinished shape keeps drawing that shape.
    if (wip && (m === "draw" || m === "exclude")) return;
    const target: Mode = (m === "draw" || m === "exclude") && pausedRef.current ? pausedRef.current.mode : m;
    modeRef.current = target; setModeState(target); lockMap(target); setDrawing(false);
    if (target === "draw" || target === "exclude") {
      const p = pausedRef.current;
      kind.current = isLine ? "line" : target === "exclude" ? "exclude" : "include";
      hidePaused(); setPause(null);
      d.setMode("static"); d.setMode(drawModeName());
      if (p) replay(p);
    } else {
      d.setMode(target === "edit" ? "select" : "static");
      if (wip) { setPause(wip); showPaused(); }
    }
    sync();
  };
  const key = (k: string) => modeObjs.current[drawModeName()]?.onKeyUp({ key: k, heldKeys: [], preventDefault: () => {} });
  const finish = () => { if (pausedRef.current) switchMode(pausedRef.current.mode); key("Enter"); };
  const discardPaused = () => { hidePaused(); setPause(null); };
  const cancel = () => {
    if (pausedRef.current && !drawing) { discardPaused(); return; }
    key("Escape"); const d = drawRef.current; d.setMode("static"); d.setMode(drawModeName()); setDrawing(false); sync();
  };
  const undoPoint = () => {
    const p = pausedRef.current;
    if (p && !drawing) {
      hidePaused();
      if (p.coords.length <= 1) { setPause(null); return; }
      setPause({ ...p, coords: p.coords.slice(0, -1) }); showPaused(); return;
    }
    const m = modeObjs.current[drawModeName()];
    try { if (m.undoSize() > 0) { m.undo(); sync(); return; } } catch (_) { /* fall through */ }
    cancel();
  };
  const restore = (features: any[]) => {
    const d = drawRef.current;
    const cur = d.getMode();
    d.setMode("static"); d.clear();
    if (features.length) d.addFeatures(JSON.parse(JSON.stringify(features)));
    if (pausedRef.current) showPaused();
    d.setMode(cur); setSelected(null); sync();
  };
  const undo = () => { if (hist.current.length <= 1) return; hist.current.pop(); restore(hist.current[hist.current.length - 1]); onEdit?.(); };
  const del = () => { const d = drawRef.current; if (!d || selected == null) return; d.deselectFeature(selected); d.removeFeatures([selected]); setSelected(null); push(); sync(); };
  const clearAll = () => { if ((!shapes.length && !paused) || !window.confirm("Clear all measurements?")) return; setPause(null); restore([]); push(); };

  const sum = (k: Kind) => shapes.filter((s) => s.kind === k).reduce((a, s) => a + s.qty, 0);
  const inc = sum("include"), exc = sum("exclude"), lin = sum("line");
  const signedDraft = drawing ? (kind.current === "exclude" ? -draft : draft) : 0;
  const total = Math.max(0, (isLine ? lin : inc - exc) + signedDraft);
  const hasMain = isLine ? lin > 0 : inc > 0;
  const submit = () => {
    const fs = feats();
    onSubmit(fs.map(strip), fs.map((f: any) => ({ label: f.properties.label, type: f.properties.kind, geometry: f.geometry, edited_at: new Date().toISOString() })));
  };

  const modes = isLine
    ? [{ id: "pan", label: "Pan", icon: Hand }, { id: "draw", label: "Draw line", icon: Spline }]
    : [{ id: "pan", label: "Pan", icon: Hand }, { id: "draw", label: "Draw area", icon: PenLine }, { id: "exclude", label: "Exclude", icon: MinusSquare }];
  const btn = "h-11 min-w-[44px] px-3 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5 border transition-colors disabled:opacity-40";
  const help = paused && !drawing ? HELP.paused : mode === "draw" && isLine ? HELP.drawLine : HELP[mode];
  const minPts = isLine ? 2 : 3;
  const canFinish = drawing ? pts >= minPts : !!paused && paused.coords.length >= minPts;
  const pending = drawing || !!paused;
  const pendingMain = pending && (drawing ? kind.current : paused.kind) !== "exclude";
  // Completes any valid unfinished shape, then submits the whole measurement.
  const finishMeasurement = () => {
    if (pending && !canFinish) {
      if (!window.confirm("Your unfinished shape has too few points. Discard it and continue?")) return;
      cancel();
    } else if (pending) finish();
    const fs = feats();
    if (!fs.some((f: any) => f.properties.kind !== "exclude")) { sync(); return; }
    submit();
  };
  const canSubmit = ready && !busy && (hasMain || (pendingMain && canFinish));

  return (
    <div className="flex flex-col gap-3" data-testid="measure-step">
      <div className={`grid gap-1.5 ${isLine ? "grid-cols-[1fr_1fr_1.4fr]" : "grid-cols-[1fr_1fr_1fr_1.4fr]"}`} role="toolbar" aria-label="Map mode">
        {modes.map((m: any) => (
          <button key={m.id} onClick={() => switchMode(m.id)} disabled={!ready} aria-pressed={mode === m.id} data-testid={`map-mode-${m.id}`}
            className={`${btn} flex-col !gap-0.5 h-14 text-[11px] whitespace-nowrap !px-1 ${mode === m.id ? "brand-btn border-transparent" : "bg-white hover:bg-slate-50"}`}>
            <m.icon className="h-4 w-4" />{m.label}
          </button>
        ))}
        <button onClick={finishMeasurement} disabled={!canSubmit} data-testid="map-finish-measurement-button"
          className={`${btn} flex-col !gap-0.5 h-14 text-[11px] leading-tight !px-1 ${canSubmit ? "bg-emerald-600 text-white border-transparent hover:bg-emerald-700" : "bg-white"}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{busy ? "Calculating…" : "Finish Measurement"}
        </button>
      </div>
      <div className="flex items-center justify-between gap-3 rounded-lg border bg-slate-50 px-3 py-2" data-testid="map-total-bar">
        <span className="text-xs text-slate-600">{isLine ? "Total length" : "Net area"}{!isLine && exc > 0 ? ` (${fmt(inc)} − ${fmt(exc)} excluded)` : ""}</span>
        <span className="font-mono font-semibold" data-testid="map-area-counter">{fmt(total)} {isLine ? "linear ft" : "sq ft"}</span>
      </div>
      <p className="text-xs text-slate-600 min-h-[32px]" data-testid="map-help-text">{help}</p>
      <div className="relative rounded-xl overflow-hidden border bg-slate-200" style={{ touchAction: "none" }}>
        <div ref={el} className="w-full h-[44dvh] min-h-[280px] max-h-[540px]" data-testid="satellite-map" />
        {!ready && <div className="absolute inset-0 grid place-items-center text-sm text-slate-600 bg-slate-100"><Loader2 className="h-5 w-5 animate-spin" /></div>}
      </div>
      {pending ? (
        <div className="flex gap-2" data-testid="drawing-actions">
          <button className={`${btn} bg-white flex-1`} onClick={finish} disabled={!canFinish} data-testid="map-complete-shape-button"><Check className="h-4 w-4" />Done with shape</button>
          <button className={`${btn} bg-white flex-1`} onClick={undoPoint} data-testid="map-undo-point-button"><Undo2 className="h-4 w-4" />Undo point</button>
          <button className={`${btn} bg-white flex-1`} onClick={cancel} data-testid="map-cancel-button"><X className="h-4 w-4" />Cancel</button>
        </div>
      ) : (
        <div className="flex gap-2" data-testid="edit-actions">
          <button className={`${btn} flex-1 ${mode === "edit" ? "brand-btn border-transparent" : "bg-white"}`} onClick={() => switchMode("edit")} disabled={!ready || !shapes.length} aria-pressed={mode === "edit"} data-testid="map-mode-edit"><MousePointer2 className="h-4 w-4" />Edit</button>
          <button className={`${btn} bg-white flex-1`} onClick={undo} disabled={!ready || hist.current.length <= 1} data-testid="map-undo-button"><Undo2 className="h-4 w-4" />Undo</button>
          <button className={`${btn} bg-white flex-1`} onClick={del} disabled={selected == null} data-testid="map-delete-button"><Trash2 className="h-4 w-4" />Delete</button>
          <button className={`${btn} bg-white flex-1`} onClick={clearAll} disabled={!shapes.length} data-testid="map-clear-button"><Eraser className="h-4 w-4" />Clear</button>
        </div>
      )}
      {shapes.length > 0 && (
        <ul className="text-xs flex flex-wrap gap-1.5" data-testid="shape-list">
          {shapes.map((s) => (
            <li key={s.id} className={`rounded-full px-2.5 py-1 border ${s.kind === "exclude" ? "border-red-300 bg-red-50 text-red-800" : s.kind === "line" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-teal-300 bg-teal-50 text-teal-900"}`}>
              {s.label}: {fmt(s.qty)} {s.kind === "line" ? "ft" : "sq ft"}
            </li>
          ))}
        </ul>
      )}
      {errors?.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="geometry-errors">
          <ul className="list-disc pl-4 space-y-0.5">{errors.map((e: string) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      {ready && !hasMain && <p className="text-xs text-slate-700" data-testid="measure-hint">{!isLine && exc > 0 ? "Exclusions alone can’t be priced — draw the area to be serviced with “Draw area”." : isLine ? "Draw at least one line to see your estimate." : "Draw at least one area to see your estimate."}</p>}
      <p className="text-[11px] text-slate-500 pb-safe">Approximate horizontal measurement from satellite imagery (not sloped surfaces). It is re-measured on our server.</p>
    </div>
  );
}
